import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InboxIcon from "@mui/icons-material/Inbox";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { toast } from "react-toastify";

import { GlobalSearchBar } from "../Components/ERPList";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";
import { schedulingApi } from "../Components/schedulingApi";

import styles from "./AllEnquiries.module.scss";

const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const EVENT_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "class", label: "Class" },
  { value: "exam", label: "Exam" },
  { value: "seminar", label: "Seminar" },
  { value: "unit", label: "Unit" },
];

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { boxShadow: "none" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
  },
};

function formatScheduled(scheduled = []) {
  if (!scheduled.length) return "—";
  return (
    scheduled
      .slice(0, 5)
      .map((s) => {
        const start = s.start_datetime
          ? new Date(s.start_datetime).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
          : "—";
        return start;
      })
      .join("; ") + (scheduled.length > 5 ? ` (+${scheduled.length - 5} more)` : "")
  );
}

export default function EventApprovalsPage() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [actingId, setActingId] = useState(null);
  const [options, setOptions] = useState({ courses: [], lecturers: [], classrooms: [] });

  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedGlobalSearch = useDebouncedValue(globalSearch, 400);
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [filterEventType, setFilterEventType] = useState("");
  const debouncedFilterEventType = useDebouncedValue(filterEventType, 400);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [reloadNonce, setReloadNonce] = useState(0);

  const loadOptions = useCallback(async () => {
    try {
      const data = await schedulingApi.getOptions();
      if (data?.error) return;
      setOptions({
        courses: data.courses || [],
        lecturers: data.lecturers || [],
        classrooms: data.classrooms || [],
      });
    } catch (_) {}
  }, []);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedGlobalSearch, debouncedFilterEventType, pageSize, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const data = await schedulingApi.listPendingApprovalEventsPaginated({
          page: currentPage,
          page_size: pageSize,
          search: debouncedGlobalSearch.trim() || undefined,
          event_type: debouncedFilterEventType || undefined,
          sort_by: sortConfig.key || "created_at",
          sort_dir: sortConfig.direction,
        });
        if (cancelled) return;
        if (data?.error) {
          setErrorMessage(data.error);
          toast.error(data.error);
          setEvents([]);
          setTotalCount(0);
          return;
        }
        setEvents(data.events || []);
        setTotalCount(data.total ?? 0);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.message || "Failed to load pending sessions";
        setErrorMessage(msg);
        toast.error(msg);
        setEvents([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    pageSize,
    debouncedGlobalSearch,
    debouncedFilterEventType,
    sortConfig.key,
    sortConfig.direction,
    reloadNonce,
  ]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setGlobalSearch("");
    setFilterEventType("");
    setSortConfig({ key: "created_at", direction: "desc" });
    setCurrentPage(1);
    toast.info("Filters cleared.");
  };

  const handleApprove = async (eventId, e) => {
    e?.stopPropagation();
    setActingId(eventId);
    try {
      const data = await schedulingApi.approveEvent(eventId);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("Session approved.");
      setReloadNonce((n) => n + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to approve");
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (eventId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Reject this event? It will no longer appear on the calendar.")) return;
    setActingId(eventId);
    try {
      const data = await schedulingApi.rejectEvent(eventId);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("Session rejected.");
      setReloadNonce((n) => n + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to reject");
    } finally {
      setActingId(null);
    }
  };

  const getCourseName = (id) =>
    (options.courses || []).find((c) => c.id === Number(id))?.name ?? (id ? `#${id}` : "—");
  const getLecturerName = (id) => {
    const u = (options.lecturers || []).find((x) => x.id === Number(id));
    return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—" : id ? `#${id}` : "—";
  };
  const getRoomCode = (id) =>
    (options.classrooms || []).find((r) => r.id === Number(id))?.room_code ?? (id ? `#${id}` : "—");

  const displayData = events;

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={styles.pageTitle}>
                  Session approvals
                </Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  Review and approve or reject pending sessions. Search and filters apply on the server.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box className={styles.header}>
            <GlobalSearchBar
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search title, type, ID, course, lecturer, room…"
            />
            <Box className={styles.headerActions}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterOpen(!filterOpen)}
                variant="outlined"
                size="small"
              >
                {filterOpen ? "Hide filters" : "Show filters"}
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => setReloadNonce((n) => n + 1)}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {filterOpen ? (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Session type</InputLabel>
                      <Select
                        value={filterEventType}
                        label="Session type"
                        onChange={(e) => {
                          setFilterEventType(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        {EVENT_TYPE_OPTIONS.map((o) => (
                          <MenuItem key={o.value || "all"} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} className={styles.filterActions}>
                    <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </FilterPaper>
            </Fade>
          ) : null}

          {loading && (
            <Box className={styles.loadingContainer}>
              <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={400} />
            </Box>
          )}

          {errorMessage && !loading && (
            <Typography color="error" align="center" sx={{ my: 4 }}>
              {errorMessage}
            </Typography>
          )}

          {!loading && !errorMessage && displayData.length === 0 && (
            <Box className={styles.emptyState}>
              <PendingActionsIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No sessions pending approval
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting search or filters, or check back later.
              </Typography>
            </Box>
          )}

          {!loading && !errorMessage && displayData.length > 0 && (
            <>
              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing{" "}
                  {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}–
                  {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={Math.min(currentPage, totalPages)}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>

              <Paper variant="outlined" className={styles.tableContainer}>
                <Table stickyHeader size="small" className={styles.enquiriesTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.colId} sortDirection={sortConfig.key === "id" ? sortConfig.direction : false}>
                        <TableSortLabel
                          active={sortConfig.key === "id"}
                          direction={sortConfig.key === "id" ? sortConfig.direction : "asc"}
                          onClick={() => requestSort("id")}
                        >
                          ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }} sortDirection={sortConfig.key === "title" ? sortConfig.direction : false}>
                        <TableSortLabel
                          active={sortConfig.key === "title"}
                          direction={sortConfig.key === "title" ? sortConfig.direction : "asc"}
                          onClick={() => requestSort("title")}
                        >
                          Title
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={styles.colStatus} sortDirection={sortConfig.key === "event_type" ? sortConfig.direction : false}>
                        <TableSortLabel
                          active={sortConfig.key === "event_type"}
                          direction={sortConfig.key === "event_type" ? sortConfig.direction : "asc"}
                          onClick={() => requestSort("event_type")}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Course</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Lecturer</TableCell>
                      <TableCell sx={{ minWidth: 88 }}>Room</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Scheduled</TableCell>
                      <TableCell className={styles.colActions} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayData.map((ev) => (
                      <TableRow
                        key={ev.id}
                        hover
                        className={styles.tableRow}
                        onClick={() => navigate(`/schedule/events/${ev.id}`)}
                      >
                        <TableCell className={styles.colId}>{ev.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} noWrap title={ev.title}>
                            {ev.title || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell className={styles.colStatus} sx={{ textTransform: "capitalize" }}>
                          {ev.event_type || "—"}
                        </TableCell>
                        <TableCell>{getCourseName(ev.course_id)}</TableCell>
                        <TableCell>{getLecturerName(ev.lecturer_id)}</TableCell>
                        <TableCell>{getRoomCode(ev.classroom_id)}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }} title={formatScheduled(ev.scheduled)}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                            {formatScheduled(ev.scheduled)}
                          </Typography>
                        </TableCell>
                        <TableCell className={styles.colActions} align="right" onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<CheckCircleOutlineIcon />}
                              onClick={(e) => handleApprove(ev.id, e)}
                              disabled={actingId === ev.id}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelOutlinedIcon />}
                              onClick={(e) => handleReject(ev.id, e)}
                              disabled={actingId === ev.id}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing{" "}
                  {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}–
                  {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={Math.min(currentPage, totalPages)}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
